import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';

import type { JiraEpicOption } from '@/domain/models/development.js';
import type { DevelopmentController } from '@/presentation/controllers/development-controller.js';
import type { ProjectController } from '@/presentation/controllers/project-controller.js';

export type JiraEpicChoice = {
    epicKey: string | null;
    ready: boolean;
};

interface Labels {
    notice: string;
    createEpic: string;
    linkExisting: string;
    search: string;
    empty: string;
}

interface Props {
    projectId: string | null;
    projectController: ProjectController;
    developmentController: DevelopmentController;
    labels: Labels;
    onChange: (choice: JiraEpicChoice) => void;
}

export function DevelopmentJiraEpicChoice({ projectId, projectController, developmentController, labels, onChange }: Props) {
    const [linked, setLinked] = useState<boolean | null>(null);
    const [mode, setMode] = useState<'create' | 'link'>('create');
    const [epics, setEpics] = useState<JiraEpicOption[]>([]);
    const [query, setQuery] = useState('');
    const [selectedKey, setSelectedKey] = useState<string | null>(null);

    useEffect(() => {
        if (!projectId) {
            setLinked(false);
            return;
        }
        let active = true;
        projectController.loadProjectById(projectId).then((result) => {
            if (!active) {
                return;
            }
            result.fold(
                () => setLinked(false),
                (project) => {
                    const hasJira = Boolean(project.jiraProjectKey && project.jiraBaseUrl);
                    setLinked(hasJira);
                    if (!hasJira) {
                        return;
                    }
                    developmentController.listProjectJiraEpics(projectId).then((listed) => {
                        if (!active) {
                            return;
                        }
                        listed.fold(
                            () => setEpics([]),
                            (value) => setEpics(value.epics)
                        );
                    });
                }
            );
        });
        return () => {
            active = false;
        };
    }, [projectController, developmentController, projectId]);

    useEffect(() => {
        if (linked !== true) {
            onChange({ epicKey: null, ready: true });
            return;
        }
        if (mode === 'create') {
            onChange({ epicKey: null, ready: true });
            return;
        }
        onChange({ epicKey: selectedKey, ready: selectedKey !== null });
    }, [linked, mode, selectedKey, onChange]);

    if (!projectId || linked === null) {
        return null;
    }
    if (!linked) {
        return (
            <Typography variant="body2" data-testid="development-jira-notice">
                {labels.notice}
            </Typography>
        );
    }
    const needle = query.trim().toLowerCase();
    const visible = needle === '' ? epics : epics.filter((epic) => epic.key.toLowerCase().includes(needle) || epic.summary.toLowerCase().includes(needle));
    return (
        <Box data-testid="development-jira-choice">
            <RadioGroup
                value={mode}
                onChange={(event) => {
                    const next = event.target.value === 'link' ? 'link' : 'create';
                    setMode(next);
                    if (next === 'create') {
                        setSelectedKey(null);
                    }
                }}
            >
                <FormControlLabel value="create" control={<Radio />} label={labels.createEpic} />
                <FormControlLabel value="link" control={<Radio />} label={labels.linkExisting} />
            </RadioGroup>
            {mode === 'link' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    <TextField data-testid="development-jira-search" label={labels.search} value={query} size="small" onChange={(event) => setQuery(event.target.value)} />
                    {visible.length === 0 && <Typography variant="body2">{labels.empty}</Typography>}
                    {visible.map((epic) => (
                        <FormControlLabel
                            key={epic.key}
                            control={<Radio checked={selectedKey === epic.key} onChange={() => setSelectedKey(epic.key)} />}
                            label={`${epic.key} ${epic.summary}`}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}
